"use client";

import React from "react";
import * as Toast from "@radix-ui/react-toast";
import clsx from "clsx";
import {IoCloseSharp} from "react-icons/io5";
import {truncateNotificationDescription} from "@/components/notifications/NotificationDescription";
import {
    NotificationCategoryBadge,
} from "@/components/notifications/NotificationCategoryBadge";
import {NotificationIcon} from "@/components/notifications/NotificationIcon";
import {resolveAccountNotificationContent} from "@/components/notifications/notificationContent";
import type {NotificationDictionary} from "@/components/notifications/NotificationDrawer";
import type {Locale} from "@/i18n-config";
import type {AccountNotification} from "@/utils/notifications/account/types";
import {InlineText} from "@/components/content/common/InlineText";

export function NotificationToasts({
    children,
    notifications,
    locale,
    dictionary,
    onDismiss,
}: {
    children: React.ReactNode;
    notifications: AccountNotification[];
    locale: Locale;
    dictionary: NotificationDictionary;
    onDismiss: (notificationId: string) => void;
}) {
    return (
        <Toast.Provider duration={6_000} swipeDirection="right">
            {children}
            {notifications.map((notification) => {
                const content = resolveAccountNotificationContent(
                    notification,
                    locale,
                    dictionary,
                );

                return (
                    <Toast.Root
                        key={notification.id}
                        defaultOpen
                        onOpenChange={(open) => {
                            if (!open) {
                                onDismiss(notification.id);
                            }
                        }}
                        className="relative grid w-full grid-cols-[auto_1fr_auto] gap-x-3 rounded-xl border border-zinc-200 bg-white p-4 text-zinc-900 shadow-xl data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:slide-in-from-bottom-2"
                    >
                        <div
                            className={clsx(
                                "flex size-9 items-center justify-center rounded-full",
                                content.categoryKey === "warning"
                                    ? "bg-rose-700/10 text-rose-700"
                                    : "bg-grass-500 text-white",
                            )}
                            aria-hidden="true"
                        >
                            <NotificationIcon
                                type={notification.type}
                                categoryKey={content.categoryKey}
                            />
                        </div>
                        <div className="min-w-0 pr-2">
                            <NotificationCategoryBadge
                                categoryKey={content.categoryKey}
                                className="mb-2 text-xs uppercase tracking-wide"
                            >
                                <InlineText value={content.category} />
                            </NotificationCategoryBadge>
                            <Toast.Title className="text-sm font-semibold leading-snug">
                                <InlineText value={content.title} />
                            </Toast.Title>
                            {content.description && (
                                <Toast.Description className="mt-1 text-sm leading-relaxed text-zinc-600">
                                    <InlineText value={truncateNotificationDescription(
                                        content.description,
                                    )} />
                                </Toast.Description>
                            )}
                        </div>
                        <Toast.Close
                            aria-label={dictionary.notificationToastClose}
                            title={dictionary.notificationToastClose}
                            className="flex size-7 items-center justify-center rounded-full text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grass-500"
                        >
                            <IoCloseSharp className="size-4" aria-hidden="true" />
                        </Toast.Close>
                    </Toast.Root>
                );
            })}
            <Toast.Viewport className="fixed inset-x-4 bottom-4 z-1004 flex max-h-[calc(100vh-2rem)] flex-col gap-2 outline-none sm:left-auto sm:w-[380px]" />
        </Toast.Provider>
    );
}
